import { keepPreviousData, queryOptions } from '@tanstack/react-query'
import { z } from 'zod'

import { api } from '@/shared/api/http'
import {
  PAGE_DEFAULT_LIMIT,
  type InstallmentPlanOrderBy,
  type InstallmentPlanOut,
  type Page,
} from '@/shared/api/types'
import { isMoney, type Money, sumMatches, toCents } from '@/shared/money/money'

/** Os dois "tipos" de parcelamento (a pagar / a receber) — como no agendamento. */
export type InstallmentPlanKind = 'payable' | 'receivable'

export const installmentPlanKeys = {
  all: () => ['installment-plans'] as const,
  lists: () => [...installmentPlanKeys.all(), 'list'] as const,
  list: (p: InstallmentPlanListParams) => [...installmentPlanKeys.lists(), p] as const,
  details: () => [...installmentPlanKeys.all(), 'detail'] as const,
  detail: (id: string) => [...installmentPlanKeys.details(), id] as const,
} as const

export interface InstallmentPlanListParams {
  readonly kind?: InstallmentPlanKind
  /** Substring, sem diferenciar caixa, na descrição OU no nome do contato. */
  readonly search?: string
  readonly stakeholder_id?: string
  readonly first_due_from?: string
  readonly first_due_to?: string
  readonly order_by?: InstallmentPlanOrderBy
  readonly limit?: number
  readonly offset?: number
}

/** Default do backend: first_due_date DESCENDENTE (mais recentes primeiro). */
export const installmentPlansQueryOptions = (p: InstallmentPlanListParams = {}) =>
  queryOptions({
    queryKey: installmentPlanKeys.list(p),
    queryFn: ({ signal }) =>
      api.get<Page<InstallmentPlanOut>>(
        '/v1/installment-plans',
        { ...p, limit: p.limit ?? PAGE_DEFAULT_LIMIT },
        signal,
      ),
    placeholderData: keepPreviousData,
  })

/**
 * Detalhe de um plano (parcelas aninhadas). A lista já traz `installments`, mas
 * o detalhe busca FRESCO: as parcelas evoluem individualmente (baixa/cancelamento
 * via /v1/schedules), então o status pode ter mudado desde a listagem.
 */
export const installmentPlanQueryOptions = (id: string) =>
  queryOptions({
    queryKey: installmentPlanKeys.detail(id),
    queryFn: ({ signal }) =>
      api.get<InstallmentPlanOut>(`/v1/installment-plans/${id}`, undefined, signal),
  })

/** Mesmo validador de dinheiro do agendamento: formato válido e > 0, tipo string. */
const moneyField = z.string().refine((v) => isMoney(v) && toCents(v) > 0, {
  message: 'validation:amountPositive',
})

const rateioItem = z.object({
  categoryId: z.uuid('validation:required'),
  amount: moneyField,
})

/**
 * Formulário de parcelamento — espelha InstallmentPlanCreate.
 *
 * O campo do total chama-se `amount` (não `totalAmount`) de propósito: é o que o
 * SplitEditor genérico observa para conferir o rateio ao vivo. O rateio segue a
 * MESMA regra do agendamento (soma = total, sem categoria repetida; proporções
 * aplicadas por parcela no backend). `installmentsCount` é 2..120.
 */
export const installmentPlanFormSchema = z
  .object({
    stakeholderId: z.uuid('validation:required'),
    amount: moneyField,
    installmentsCount: z.string().refine((v) => {
      const n = Number(v)
      return Number.isInteger(n) && n >= 2 && n <= 120
    }, 'validation:installmentsRange'),
    firstDueDate: z.iso.date('validation:invalidDate'),
    categories: z.array(rateioItem).min(1).max(100),
    costCenterId: z.string(),
    description: z.string().trim().max(480),
  })
  .refine(
    (d) =>
      sumMatches(
        d.amount as Money,
        d.categories.map((c) => c.amount as Money),
      ),
    { path: ['categories'], message: 'validation:splitMismatch' },
  )
  .refine((d) => new Set(d.categories.map((c) => c.categoryId)).size === d.categories.length, {
    path: ['categories'],
    message: 'validation:duplicateCategory',
  })

export type InstallmentPlanFormValues = z.infer<typeof installmentPlanFormSchema>

interface RateioBody {
  readonly category_id: string
  readonly amount: string
}

export interface InstallmentPlanCreateBody {
  readonly kind: InstallmentPlanKind
  readonly stakeholder_id: string
  readonly total_amount: string
  readonly installments_count: number
  readonly first_due_date: string
  readonly categories: readonly RateioBody[]
  readonly cost_center_id?: string
  readonly description?: string
}

/**
 * Form → corpo do POST. `interval` é omitido (backend usa 'monthly' por default).
 * Vazios viram AUSENTES, não '': o backend distingue "não informado".
 */
export function toInstallmentPlanCreateBody(
  v: InstallmentPlanFormValues,
  kind: InstallmentPlanKind,
): InstallmentPlanCreateBody {
  return {
    kind,
    stakeholder_id: v.stakeholderId,
    total_amount: v.amount,
    installments_count: Number(v.installmentsCount),
    first_due_date: v.firstDueDate,
    categories: v.categories.map((c) => ({ category_id: c.categoryId, amount: c.amount })),
    ...(v.costCenterId !== '' && { cost_center_id: v.costCenterId }),
    ...(v.description !== '' && { description: v.description }),
  }
}

/**
 * `POST /v1/installment-plans` — EFEITO FINANCEIRO: exige Idempotency-Key.
 *
 * A chave vem de fora (useIdempotencyKey, escopo de INTENÇÃO). `operationPath`
 * põe a rota na allowlist de idempotência do http.ts. 201 = criou; 200 = replay.
 */
export function createInstallmentPlan(
  body: InstallmentPlanCreateBody,
  idempotencyKey: string,
): Promise<{ data: InstallmentPlanOut; created: boolean }> {
  return api.post<InstallmentPlanOut>('/v1/installment-plans', {
    body,
    idempotencyKey,
    operationPath: '/v1/installment-plans',
  })
}
